// Brings open tabs onto a new release.
//
// A deploy replaces the bundle on the server, but a tab that is already open keeps running the old
// one from memory - and fetches the *new* templates at runtime, so an old tab ends up running old JS
// against new markup and a new backend. Nothing in the page ever asked whether a release had gone out.
//
// The build writes the same timestamp into the VERSION constant and into /version.json (see the
// token-replacement step in azure-pipelines.yml). This polls that file, and when it no longer matches
// the bundle that is running, the tab reloads - but only on the landing page (the login state). That
// is where logout and idle timeout both land, and nothing on it can lose clinical work.
//
// Locally both values stay the literal __BUILD_TIMESTAMP__, so they always match and dev never reloads.
app.service("VersionCheckService", [
	"$interval",
	"$timeout",
	"$http",
	"$state",
	"$rootScope",
	"VERSION",
	function ($interval, $timeout, $http, $state, $rootScope, VERSION) {
		/** A static file read; cheap enough to ask every few minutes. */
		const POLL_MS = 5 * 60 * 1000;
		/** How long a reload waits for in-flight requests, before going anyway. */
		const DRAIN_MAX_MS = 5000;
		const DRAIN_STEP_MS = 250;
		/** A keypress this recent means someone is typing on the landing page. */
		const TYPING_GRACE_MS = 10 * 1000;
		const CHECK_THROTTLE_MS = 15 * 1000;
		const LANDING_STATE = "login";
		/** Remembers, for this tab, which release a reload has already been made for. */
		const RELOADED_FOR_KEY = "cattrax.versionReloadedFor";

		let started = false;
		let latestVersion = null;
		let reloading = false;
		let lastKeyAt = 0;
		let lastCheckAt = 0;

		function updateAvailable() {
			return latestVersion !== null && latestVersion !== VERSION;
		}

		function reloadedFor() {
			try {
				return window.sessionStorage.getItem(RELOADED_FOR_KEY);
			} catch (e) {
				return null;
			}
		}

		function rememberReloadFor(version) {
			try {
				window.sessionStorage.setItem(RELOADED_FOR_KEY, version);
			} catch (e) {
				// Without storage the loop guard is lost, but the reload itself still works.
			}
		}

		function check() {
			// Focus and visibility fire on every alt-tab; one look every few seconds is plenty.
			if (Date.now() - lastCheckAt < CHECK_THROTTLE_MS) {
				return;
			}
			lastCheckAt = Date.now();

			// fetch, not $http: this is not an API call, and keeping it outside the interceptors means a
			// 404/503 while Tomcat is mid-redeploy is not retried as an auth failure or uploaded as an
			// exception.
			return fetch("/version.json?t=" + Date.now(), { cache: "no-store" })
				.then(function (response) {
					return response.ok ? response.json() : null;
				})
				.then(function (data) {
					if (!data || typeof data.version !== "string") {
						return;
					}
					latestVersion = data.version;
					if ($state.current.name === LANDING_STATE) {
						reloadOnLanding();
					}
				})
				.catch(function () {
					// Offline, or the server is mid-deploy. The next tick will ask again.
				});
		}

		function reloadOnLanding() {
			if (reloading || !updateAvailable()) {
				return;
			}
			// If the server keeps answering with a version this tab has already reloaded for (a stale
			// cache somewhere between us and the browser), reloading again would only loop.
			if (reloadedFor() === latestVersion) {
				return;
			}
			// Someone mid-way through typing their login details would lose them; the next check will try
			// again. Recent typing, not a focused field: a field left focused (or filled by a password
			// manager) would otherwise hold a stale tab on the old release indefinitely.
			if (Date.now() - lastKeyAt < TYPING_GRACE_MS) {
				return;
			}

			reloading = true;
			const target = latestVersion;
			let waited = 0;

			// Logout navigates here while its server call is still in flight, and a page load would
			// cancel it. Let outstanding requests finish first.
			(function waitForRequests() {
				if ($http.pendingRequests.length > 0 && waited < DRAIN_MAX_MS) {
					waited += DRAIN_STEP_MS;
					$timeout(waitForRequests, DRAIN_STEP_MS, false);
					return;
				}
				rememberReloadFor(target);
				// The query must change for the browser to make a real request rather than a hash change.
				window.location.href = "/?v=" + Date.now() + "#/" + LANDING_STATE;
			})();
		}

		const service = {
			start: function () {
				if (started) {
					return;
				}
				started = true;

				$rootScope.$on("$stateChangeSuccess", function (event, toState) {
					if (toState.name === LANDING_STATE) {
						reloadOnLanding();
					}
				});

				// invokeApply false: the check touches no scope, so a tick must not digest the whole app.
				$interval(check, POLL_MS, 0, false);

				// A PC unlocked in the morning should not wait out the rest of a poll interval. Window focus
				// as well as visibility: a tab left logged out overnight is often still "visible" to the
				// browser, and clicking into it is the moment to catch it - before anyone starts typing.
				document.addEventListener("visibilitychange", function () {
					if (document.visibilityState === "visible") {
						check();
					}
				});
				window.addEventListener("focus", check);

				document.addEventListener(
					"keydown",
					function () {
						lastKeyAt = Date.now();
					},
					true
				);

				check();
			},
		};

		return service;
	},
]);
